import { Delete, Edit, Sort } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PSelect from "components/PSelect";
import { FirmWithLenses } from "generated/graphql";
import { useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectLenses } from "redux/slices/client";
import theme, { LinkSX } from "theme";
import { P } from "theme/typography";
import FirmDeleteDialog from "./FirmDeleteDialog";
import FirmForm from "./FirmForm";
import FirmLensPicker from "./FirmLensPicker";
import FirmTabularImport from "./FirmTabularImport";
import { selectFirmsWithLenses } from "redux/slices/client";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";

const FirmManagement = () => {
  const [importFirmDialogOpen, setImportFirmDialogOpen] = useState(false);
  const [deleteFirmDialogOpen, setFirmDeleteDialogOpen] = useState(false);
  const [editFirmDialogOpen, setEditFirmDialogOpen] = useState(false);
  const [firmLensPickerOpen, setFirmLensPickerOpen] = useState(false);
  const [currentSelectedFirm, setCurrentSelectedFirm] = useState<FirmWithLenses | undefined>(undefined);
  const [lensFilter, setLensFilter] = useState<number | undefined>(undefined);
  const lenses = useAppSelector(selectLenses);
  const allFirms = useAppSelector(selectFirmsWithLenses);
  const firms = useFirmsByLensId(lensFilter);

  const firmsToUse = lensFilter !== undefined ? firms : allFirms;

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", p: 2, height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}>
              <P variant="h5">Firm Management</P>
              <FormControl size="small" sx={{ width: 150, ml: 2 }}>
                <InputLabel variant="outlined">Lens</InputLabel>
                <PSelect
                  placeholder="All"
                  label="Lens"
                  defaultValue={0}
                  value={lensFilter}
                  onChange={(e) => {
                    if (e.target.value === 0) setLensFilter(undefined);
                    else setLensFilter(e.target.value as number);
                  }}>
                  {/* All for undefined */}
                  <MenuItem value={0}>All</MenuItem>
                  {lenses.map((lens) => (
                    <MenuItem key={lens.id} value={lens.id}>
                      {lens.name}
                    </MenuItem>
                  ))}
                </PSelect>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}>
              <Button
                size="small"
                variant="contained"
                sx={{ mr: 1 }}
                onClick={() => {
                  setImportFirmDialogOpen(true);
                }}>
                Spreadsheet Sync
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setCurrentSelectedFirm(undefined);
                  setEditFirmDialogOpen(true);
                }}>
                Add Firm
              </Button>
            </Box>
          </Box>
          <Box sx={{ minHeight: 0 }}>
            <TableContainer sx={{ height: "100%" }}>
              <Table size="small" aria-label="firm table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>External ID</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <span>Name</span>{" "}
                        <Sort
                          sx={{
                            height: 16,
                            width: 16,
                            ml: 0.5,
                            color: theme.palette.grey[400],
                            transform: "rotateX(180deg)",
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>Attorney</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Lenses</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {firmsToUse.map((firm) => (
                    <TableRow key={firm.id}>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                        }}>
                        {firm.externalId}
                      </TableCell>
                      <TableCell>{firm.name}</TableCell>
                      <TableCell>{firm.attorneyName}</TableCell>
                      <TableCell>{firm.address}</TableCell>
                      <TableCell>{firm.phoneNumber}</TableCell>
                      <TableCell
                        sx={{
                          ":hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                          flex: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setCurrentSelectedFirm(firm);
                          setFirmLensPickerOpen(true);
                        }}>
                        <Box
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}>
                          <P sx={{ ...LinkSX }}>
                            {firm.lensIds
                              .map((lensId) => lenses.find((lens) => lens.id === lensId)?.name)
                              .filter((lensName) => lensName !== undefined)
                              .join(", ") || "No Lenses"}
                          </P>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCurrentSelectedFirm(firm);
                              setEditFirmDialogOpen(true);
                            }}>
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCurrentSelectedFirm(firm);
                              setFirmDeleteDialogOpen(true);
                            }}>
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Card>
      <Dialog open={importFirmDialogOpen} onClose={() => setImportFirmDialogOpen(false)} maxWidth="md">
        <FirmTabularImport />
      </Dialog>

      {/* Edit Dialogs */}
      {deleteFirmDialogOpen && (
        <FirmDeleteDialog
          open={deleteFirmDialogOpen}
          onClose={() => setFirmDeleteDialogOpen(false)}
          firmToDelete={currentSelectedFirm}
        />
      )}
      {editFirmDialogOpen && (
        <FirmForm
          open={editFirmDialogOpen}
          onClose={() => setEditFirmDialogOpen(false)}
          firmToEdit={currentSelectedFirm}
        />
      )}
      {currentSelectedFirm && (
        <FirmLensPicker
          open={firmLensPickerOpen}
          onClose={() => setFirmLensPickerOpen(false)}
          firm={currentSelectedFirm}
        />
      )}
    </>
  );
};

export default FirmManagement;
